import { useCallback } from 'react'
import { useMutation, useQuery } from '@truepill/tpos-react-router'
import { CLEAR_COPAY_LOCK, GET_COPAY_LOCK, SET_COPAY_LOCK } from 'gql'
import useErrorToast from 'hooks/toast/useErrorToast'
import { usePlusClient } from 'providers/VisionRouter'
import { isNil } from 'ramda'
import type { Lock, CopayRequest, User, UserFirstLastName } from 'types'

type LockWithPartialUser = Omit<Lock, 'user'> & { user: Pick<User, '_id' | 'firstName' | 'lastName'> }
type CopayLockMutation = Pick<CopayRequest, '_id'> & { lock: LockWithPartialUser }

interface CopayLock {
  copayEditable: boolean
  copayLock?: LockWithPartialUser
  copayLockedBy?: UserFirstLastName
  isCopayLockedByMe: boolean
  setCopayLock: () => Promise<LockWithPartialUser | undefined>
  clearCopayLock: () => Promise<LockWithPartialUser | undefined>
}

export default function useCopayLock(copayId?: string, shouldSkip = false): CopayLock {
  const { tokenContext } = usePlusClient()
  const showErrorToast = useErrorToast(true)
  const { loading, error, data } = useQuery<{ copay: { lock?: LockWithPartialUser } }>(GET_COPAY_LOCK, {
    skip: isNil(copayId) || shouldSkip,
    variables: { copayId },
  })

  const [setCopayLockMutation] = useMutation<{
    copay?: CopayLockMutation
  }>(SET_COPAY_LOCK)

  const [clearCopayLockMutation] = useMutation<{
    copay?: CopayLockMutation
  }>(CLEAR_COPAY_LOCK)

  const setCopayLock = useCallback(async () => {
    if (!copayId) {
      return
    }

    try {
      const { data: copayLockData } = await setCopayLockMutation({ variables: { copayId } })
      return copayLockData?.copay?.lock
    } catch (error) {
      showErrorToast(`Failed to lock copay: ${error.message}`)
    }
  }, [copayId, setCopayLockMutation, showErrorToast])

  const clearCopayLock = async () => {
    if (!copayId) {
      return
    }

    try {
      const { data: copayClearData } = await clearCopayLockMutation({ variables: { copayId } })
      return copayClearData?.copay?.lock
    } catch (error) {
      showErrorToast(`Failed to unlock copay: ${error.message}`)
    }
  }

  const isCopayLockedByMe = data?.copay?.lock?.user._id === tokenContext?.id
  const copayEditable = !copayId || (!!tokenContext && (!data?.copay?.lock || isCopayLockedByMe))

  return {
    copayEditable,
    isCopayLockedByMe,
    copayLockedBy: data?.copay?.lock?.user,
    copayLock:
      loading || error || !data || (data.copay?.lock?.expiresAt ?? -1) < Date.now() ? undefined : data.copay?.lock,
    setCopayLock,
    clearCopayLock,
  }
}
